import Vue from 'vue';
import dayjs from 'dayjs';
import { callAPI, publishLocal } from '@/store/socket';
import { lastTrackedTaskOfOpenWorkspace, saveTaskToLocalStorage } from '@/store/utils';
import { refreshOthersTrackingStatus } from '@/store/othersTrackingStatus';
import { callAPIPersisted } from '@/persistedQueue';
import trackerEngine from '@/trackingEngine';
import { scheduleResume, scheduleResumeAfterMinutes, cancelScheduledResume } from '@/store/pauseManager';
import store from '@/store';
import { noProposeTracking } from '@/utils/constantHolder';
import i18n from '@/i18n';

// Bots are project members so their comments and events pass permission checks,
// but work cannot be handed to them.
const withoutBots = (members, getUser) => members.filter(m => !getUser(m.userId)?.botClassId);

const WINDOW_CHOOSE_SHARE_SCREEN = 'choose_to_share';
const WINDOW_RETRY_CHOOSE_SHARE_SCREEN = 'retry_choose_to_share';
const WINDOW_CANCEL_PROVIDE_SCREEN = 'cancel_provide_screen';
const WINDOW_REQUEST_NOTIFICATION = 'request_notification';
const WINDOW_REQUEST_IDLE = 'request_idle';
const WINDOW_TRACK_STARTED = 'track_started';
const WINDOW_RESET_PERMISSION = 'reset_permission';
const WINDOW_CHROME_REQUIRE = 'is_not_chrome_browser';

const FAVICONS = {
  default: '/favicon-57-orange.png',
  active: '/favicon.gif',
  warning: '/favicon-warning.png',
  error: '/favicon-error.png',
};

// the 'tracking started' notice closes itself unless the user hovered it (holdNotifyWindow)
let trackStartedCloseTimer = null;

let ipcRenderer;
if (window.APP_TRON) {
  const DOWNTIME_NAME = 'downtime_tracklify';
  // eslint-disable-next-line prefer-destructuring
  ipcRenderer = window.APP_TRON.ipcRenderer;
  ipcRenderer.on('muteDownTime', async () => {
    // Mute tomorrow
    const midNight = new Date().setHours(24, 0, 0, 0) - Date.now();
    localStorage.setItem(DOWNTIME_NAME, +new Date(+new Date() + +midNight));
  });
  ipcRenderer.on('downtimePopupRender', () => {
    const downTime = localStorage.getItem(DOWNTIME_NAME);
    const lastTask = store.state.task.lastTrackedTask;
    const activeTask = store.state.task.activeTrackingTask;

    if (!lastTask || activeTask || +downTime > +new Date()) {
      return;
    }
    ipcRenderer.send('showDowntimeWindow', !!lastTask);
  });
  ipcRenderer.on('startLast', () => {
    const lastTask = store.state.task.lastTrackedTask ?? lastTrackedTaskOfOpenWorkspace();
    const activeTask = store.state.task.activeTrackingTask;
    if (lastTask && !activeTask) {
      store.dispatch('task/startTracking', {
        task: lastTask,
        retry: true,
        notify: true,
      });
    }
  });
  ipcRenderer.on('openTaskDialogFromNotification', (_, { task }) => {
    if (task) {
      store.dispatch('project/actionGetTaskAndOpen', task);
    }
    store.commit('user/showNextNotifyWindow');
  });
  ipcRenderer.on('trackingStop', (_, reason) => {
    store.dispatch('task/stopTracking', { action: reason });
  });
}

// const handlerNotify
export default {
  namespaced: true,
  state: {
    activeTrackingTask: null,
    notifyWindow: null,
    metaInfoNotify: null,
    showTrackingInfo: false,
    isManualStop: false,
    isForceStop: false,
    lastTrackedTask: null,
  },
  mutations: {
    mutationTrackingTask(state, task) {
      state.activeTrackingTask = task;

      if (task) {
        state.lastTrackedTask = task;
      }
    },
    mutationShowTrackingInfo(state, flag) {
      state.showTrackingInfo = flag;
    },
    mutationRefreshOthersTrackingStatus(state, { apiTtbus, myUserId }) {
      refreshOthersTrackingStatus(state.activeTrackingTask.taskTrackedByUsers, apiTtbus, myUserId);
    },
    mutationUpdateDataInTrackingTask(state, data) {
      if (data?.id === state.activeTrackingTask?.id) {
        state.activeTrackingTask = { ...state.activeTrackingTask, ...data };
      }
    },
    mutationNotifyWindow(state, nameWindow) {
      if (window.APP_TRON || window.devicePlatformIsMobile) {
        return; // we don't need these notification windows in APP, because there are no permission screens
      }
      state.notifyWindow = nameWindow;
    },
    mutationManualStop(state, flag) {
      state.isManualStop = flag;
    },
    mutationForceStop(state, flag) {
      state.isForceStop = flag;
    },
    setMetaNotify(state, data) {
      state.metaInfoNotify = data;
    },
    mutationUpdateTimeSlot(state, timeSlot) {
      if (state.activeTrackingTask?.id === timeSlot.taskId) {
        const task = state.activeTrackingTask;
        const ttbuIndex = task.taskTrackedByUsers ? task.taskTrackedByUsers.findIndex(
          t => t.id === timeSlot.taskTrackedByUserId,
        ) : -1;

        if (ttbuIndex === -1) {
          console.error('TTBU not found on mutationUpdateTimeSlot');
          return;
        }

        const ttbu = task.taskTrackedByUsers[ttbuIndex];
        ttbu.timeSlots = [timeSlot];
        state.activeTrackingTask.taskTrackedByUsers[ttbuIndex] = ttbu;
      }
    },
  },
  getters: {
    assigneeSelectList() {
      return (task, assigneeList, getUser) => {
        if (!assigneeList) {
          return [];
        }
        let candidates = withoutBots(JSON.parse(JSON.stringify(assigneeList)), getUser);
        candidates.sort((a, b) => {
          if (store.state.user.user.id === a.userId) return -1;
          if (store.state.user.user.id === b.userId) return 1;

          if ((a.lastEventActivity || '0') < (b.lastEventActivity || '0')) return 1;
          if ((a.lastEventActivity || '0') > (b.lastEventActivity || '0')) return -1;
          return 0;
        });

        candidates = candidates.reduce((acc, a) => {
          if (a.userId !== task.assigneeId) {
            return [...acc, a.userId];
          }
          return acc;
        }, []);

        // add empty string for unassigned option if task has an assignee
        if (task.assigneeId) {
          candidates.unshift('');
        }

        return candidates.map(c => (c ? getUser(c) : null));
      };
    },
    executorSelectList() {
      return (task, executorList, getUser) => {
        if (!executorList) {
          return [];
        }
        let candidates = withoutBots(JSON.parse(JSON.stringify(executorList)), getUser);
        candidates.sort((a, b) => {
          if (store.state.user.user.id === a.userId) return -1;
          if (store.state.user.user.id === b.userId) return 1;

          if ((a.lastEventActivity || '0') < (b.lastEventActivity || '0')) return 1;
          if ((a.lastEventActivity || '0') > (b.lastEventActivity || '0')) return -1;
          return 0;
        });

        candidates = candidates.reduce((acc, a) => {
          if (a.userId !== task.executorId) {
            return [...acc, a.userId];
          }
          return acc;
        }, []);

        // add empty string for unassigned option if task has an executor
        if (task.executorId) {
          candidates.push('');
        }

        return candidates.map(c => (c ? getUser(c) : null));
      };
    },
  },

  actions: {
    async saveScreenshot({ commit }, { timeSlot, source }) {
      const sentTimeSlot = {
        ...timeSlot,
      };

      if (!sentTimeSlot.accepted) {
        const ttbu = timeSlot?.taskTrackedByUsers?.find(t => t.id === sentTimeSlot.taskTrackedByUserId);
        if (ttbu) {
          publishLocal({
            event: 'updateTTBU',
            data: {
              ttbu: {
                id: sentTimeSlot.taskTrackedByUserId,
                taskId: sentTimeSlot.taskId,
                totalSeconds: ttbu.totalSeconds - sentTimeSlot.actualTimeSeconds,
              },
            },
          });
        }
        delete sentTimeSlot.data;
        delete sentTimeSlot.smallData;
      }
      delete sentTimeSlot.images;

      const screenshot = sentTimeSlot.accepted && {
        id: sentTimeSlot.id,
        capturedAt: sentTimeSlot.capturedAt,
        createdAt: sentTimeSlot.createdAt,
        taskId: sentTimeSlot.taskId,
        trackStarted: sentTimeSlot.trackStarted,
        userId: sentTimeSlot.userId,
        workspaceId: sentTimeSlot.workspaceId,
        data: sentTimeSlot.data,
      };

      if (store.state.project.enableScreenshots && sentTimeSlot.accepted) {
        publishLocal({
          event: 'addScreenshot',
          data: {
            screenshot: {
              ...sentTimeSlot,
              user: store.state.user.user,
            },
          },
        });
        commit('user/mutationAddNewScreenshot', screenshot, { root: true });
      }

      callAPIPersisted('postScreenshot', {
        timeSlot: sentTimeSlot,
        source,
      });
    },
    async setLabelForTask(_, {
      task, label, add, clear,
    }) {
      publishLocal({
        event: 'setLabelForTask',
        data: {
          task,
          label,
          add,
          clear,
        },
      });
      await callAPI({
        method: 'setLabelForTask',
        params: {
          task,
          label,
          add,
          clear,
        },
        persisted: true,
      });
    },
    async startTracking({ commit, dispatch, state }, data) {
      cancelScheduledResume();
      if (!window.APP_TRON) {
        const localDisableTracking = localStorage.getItem(noProposeTracking);
        if (localDisableTracking === 'true' && !data.manual && !state.activeTrackingTask) {
          console.log('🥵🥵🥵🥵🥵🥵🥵 => Tracking is disabled by localStorage', localDisableTracking);
          return null;
        }
        localStorage.setItem(noProposeTracking, 'false');
      }

      if (!window.APP_TRON && store.state.user.appOpenState && !data.manual) {
        console.log('🥵🥵🥵🥵🥵🥵🥵 => App is open, not starting tracking', !window.APP_TRON, store.state.user.appOpenState, !data.manual);
        return null;
      }

      const { task } = data;
      const isRetry = data.retry;
      const { user } = store.state.user;
      const enableScreenshots = store.state.project?.enableScreenshots
        ?? task.project?.enableScreenshots;

      if (typeof enableScreenshots !== 'boolean') {
        console.error('No doingScreenshots in task. This will cause crash');
        return null;
      }

      const hasIdleDetector = 'IdleDetector' in window;
      let changedTask = false;
      if (state.activeTrackingTask && task.id === state.activeTrackingTask.id) {
        return null;
      }
      commit('mutationManualStop', false);
      commit('mutationForceStop', false);

      if (window.APP_TRON) {
        ipcRenderer.send('hideNotificationWindow');
      }

      if (!hasIdleDetector && !window.APP_TRON) {
        commit('mutationNotifyWindow', WINDOW_CHROME_REQUIRE);
        return false;
      }

      if (state.activeTrackingTask) {
        changedTask = true;
        await dispatch('stopTracking', { trigEvent: 'pause', changedTask });
      }

      const handlerErrorTracking = {
        RETRY: () => {
          commit('mutationTrackingTask', null);
          commit('mutationNotifyWindow', WINDOW_RETRY_CHOOSE_SHARE_SCREEN);
          dispatch('startTracking', {
            task,
            retry: true,
          });
        },
        CANCEL: () => {
          commit('mutationTrackingTask', null);
          commit('mutationNotifyWindow', WINDOW_CANCEL_PROVIDE_SCREEN);
          dispatch('changeFavicon', 'default');
        },
      };

      if (!isRetry && !changedTask && !window.APP_TRON) {
        dispatch('changeFavicon', 'warning');
        dispatch('changeTitle', '⛔ Not tracking - Awaiting for action');

        const showIdleTooltip = setTimeout(() => commit('mutationNotifyWindow', WINDOW_REQUEST_IDLE), 500);
        const idlePermission = await trackerEngine.checkIdlePermission();

        clearTimeout(showIdleTooltip);
        if (!idlePermission) {
          commit('mutationTrackingTask', null);
          commit('mutationNotifyWindow', WINDOW_RESET_PERMISSION);
          dispatch('changeFavicon', 'default');
          return null;
        }
        if (enableScreenshots) {
          commit('mutationNotifyWindow', WINDOW_CHOOSE_SHARE_SCREEN);
        } else {
          commit('mutationNotifyWindow', null);
        }
      }


      const userTTBU = task.taskTrackedByUsers
        && task.taskTrackedByUsers[0]
        && task.taskTrackedByUsers.find(x => x.userId === user.id);

      const lastTimeSlot = userTTBU && userTTBU.timeSlots && userTTBU.timeSlots[0];

      const userTTBUIdx = userTTBU && task.taskTrackedByUsers.findIndex(x => x.id === userTTBU.id);

      const timeSlot = await trackerEngine.activateTracking({
        task,
        user,
        prevTimeSlot: lastTimeSlot,
        changedTask,
        enableScreenshots,
      });

      if (handlerErrorTracking[timeSlot]) {
        handlerErrorTracking[timeSlot]();
        return null;
      }

      publishLocal({
        event: 'updateTimeSlot',
        note: `${lastTimeSlot ? 'Last' : 'New'} timeSlot, from startTrack`,
        data: { ...timeSlot },
      });

      commit('mutationTrackingTask', task);

      publishLocal({
        event: 'updateTask',
        data: {
          id: task.id,
          projectId: task.projectId,
          ...(task.parentTaskId ? { parentTaskId: task.parentTaskId } : {}),
        },
      });

      if (userTTBUIdx !== undefined && userTTBUIdx !== -1) {
        publishLocal({
          event: 'updateTTBU',
          data: {
            ttbu: {
              id: state.activeTrackingTask.taskTrackedByUsers[userTTBUIdx].id,
              taskId: state.activeTrackingTask.id,
              trackingOnline: true,
              userId: user.id,
              lastStarted: timeSlot.trackStarted,
            },
            src: 'store/task/startTracking',
          },
        });
      }
      console.log('🌟🌟🌟🌟🌟 CALLING START TRACK');
      callAPIPersisted('startTrack', { taskId: task.id, newTimeSlot: timeSlot }, (resp) => {
        if (resp.error) {
          dispatch('stopTracking', { action: 'force_stop' });
        } else if (resp.success && state.activeTrackingTask?.id === task.id) {
          commit('mutationRefreshOthersTrackingStatus', { apiTtbus: resp.taskTrackedByUsers, myUserId: user.id });
        }
      });

      dispatch('scheduleTrackCheckIn');
      dispatch(
        'changeTitle',
        `⏳ Tracking - ${task.textMD.length < 5 ? task.textMD.slice(0, 5) : task.textMD
        } - Tracklify`,
      );

      if (window.APP_TRON) {
        ipcRenderer.send('activeStartLastTaskItem', false);
        ipcRenderer.send('activePauseLastTaskItem', true);
        ipcRenderer.send('trackingStart');
      }

      if (!changedTask && !data.force) {
        if (Notification.permission === 'default') {
          commit('mutationNotifyWindow', WINDOW_REQUEST_NOTIFICATION);
          const notificationPermission = await trackerEngine.checkNotifyPermission();
          console.log(notificationPermission);
        }

        commit('mutationNotifyWindow', null);
        dispatch('changeFavicon', 'active');
        if (window.APP_TRON) {
          ipcRenderer.send('hideNotificationWindow');
        }
      }

      if (data.notify && window.APP_TRON) {
        const project = task.projectId
          && store.state.project.projectsList?.find(x => x.id === task.projectId);
        const name = task.textMD;
        const hidTask = task.hid;
        ipcRenderer.send('notifyResumedTask', {
          name,
          hid: hidTask,
          projectName: project?.name,
          timestamp: +new Date(),
        });
      }

      if (!changedTask && !localStorage.getItem('hide-share-window') && enableScreenshots) {
        commit('mutationNotifyWindow', WINDOW_TRACK_STARTED);

        clearTimeout(trackStartedCloseTimer);
        trackStartedCloseTimer = setTimeout(() => {
          commit('mutationNotifyWindow', null);
        }, 15000);
      }

      store.dispatch('user/actionGetMyRecentTasks', null, { root: true });
      return null;
    },
    async actionTrackCheckIn(_, req) {
      await callAPI({
        method: 'trackCheckin',
        params: req,
        first: false,
        dontWaitIfNoConn: true,
        dontRetry: true,
      });
    },
    async stopTracking({ commit, dispatch, state }, data) {
      cancelScheduledResume();

      if (!state.activeTrackingTask) {
        console.log('We dont have active task');
        return;
      }

      // tracking cut by closing the window is resumed on the next app start; a stop by the user is not
      if (['windowClose', 'appUpdate'].includes(data.causedBy)) {
        localStorage.setItem('RESUME_LAST_TASK', 'true');
      } else {
        localStorage.removeItem('RESUME_LAST_TASK');
      }

      if (data.force) {
        commit('mutationForceStop', true);
      }

      if (data.manual) {
        commit('mutationManualStop', true);
      }

      if (window.APP_TRON) {
        ipcRenderer.send('activeStartLastTaskItem', true);
        ipcRenderer.send('activePauseLastTaskItem', false);
      }

      const trigger = data.trigEvent || 'pause';
      const { action } = data;
      const currentProject = store.state.project.project;
      const timeSlot = trackerEngine.deactivateTracking(data.changedTask, trigger, action);
      const handlerAction = {
        time: () => {
          if (window.APP_TRON) {
            ipcRenderer.send('notifyUserByPopup', 'Tracking stopped: Computer suspended or hanged up', 'stop');
          }
        },
        idle: (stoppedTask) => {
          if (window.APP_TRON) {
            ipcRenderer.send('notifyUserByPopup', i18n.global.t('system.__tracking_stopped'), 'stop', {
              message: stoppedTask.textMD,
              taskRef: `${stoppedTask.project.name}/${stoppedTask.hid}`,
            });
          }
        },
        denied_idle: () => { },
        click_stop_share: () => {
          dispatch('changeFavicon', 'error');
          dispatch('changeTitle', 'Tracking stopped');
        },
        // force_stop: () => {},
      };

      if (state.activeTrackingTask.projectId === currentProject.id) {
        const { user } = store.state.user;
        const userTTBUIdx = state.activeTrackingTask.taskTrackedByUsers
          && state.activeTrackingTask.taskTrackedByUsers[0]
          && state.activeTrackingTask.taskTrackedByUsers.findIndex(x => x.userId === user.id);

        publishLocal({
          event: 'updateTimeSlot',
          note: 'timeSlot on stopTrack',
          data: timeSlot,
        });
        publishLocal({
          event: 'updateTask',
          data: {
            id: state.activeTrackingTask.id,
            projectId: state.activeTrackingTask.projectId,
            taskTrackedByUsers: state.activeTrackingTask.taskTrackedByUsers,
            ...(state.activeTrackingTask.parentTaskId ? { parentTaskId: state.activeTrackingTask.parentTaskId } : {}),
          },
        });

        if (userTTBUIdx !== undefined && userTTBUIdx !== -1) {
          publishLocal({
            event: 'updateTTBU',
            data: {
              ttbu: {
                id: state.activeTrackingTask.taskTrackedByUsers[userTTBUIdx].id,
                taskId: state.activeTrackingTask.id,
                trackingOnline: false,
              },
            },
          });
        }
      }
      saveTaskToLocalStorage(state.activeTrackingTask);

      callAPIPersisted('stopTrack', {
        taskId: state.activeTrackingTask.id,
        timeSlot,
        trigEvent: trigger,
        action: data.action,
      });

      const pausedTask = state.activeTrackingTask;

      if (!data.changedTask) {
        commit('mutationTrackingTask', null);
        dispatch('changeFavicon', 'default');
        dispatch('changeTitle', 'Tracklify.com');
        if (window.APP_TRON) {
          ipcRenderer.send('trackingStop', action);
        }
      }

      if (data.pauseUntil === 'windowActivation') {
        scheduleResume(pausedTask, dispatch);
      } else if (data.pauseForMinutes) {
        scheduleResumeAfterMinutes(data.pauseForMinutes, pausedTask, dispatch);
      }
      if (handlerAction[action]) {
        if (!window.APP_TRON) {
          window.activeSw.showNotification('Tracking stopped', {
            body: 'Tracking stopped, go back to Tracklify.com',
            icon: '/favicon-192-orange.png',
            tag: 'stop-tracking',
            renotify: true,
            silent: true,
          });
        }
        // the active task is already cleared here: the handlers get the one just stopped
        handlerAction[action](pausedTask);
      }
    },
    async taskLikedControl(_, taskId) {
      const res = await callAPI({
        method: 'taskLikedControl',
        params: {
          taskId,
        },
        persisted: true,
      });
      return res;
    },
    scheduleTrackCheckIn({ dispatch, state }) {
      if (this.scheduledCheckIn) {
        clearTimeout(this.scheduledCheckIn);
        this.scheduledCheckIn = null;
      }

      if (!state.activeTrackingTask) {
        return null;
      }

      this.scheduledCheckIn = setTimeout(() => {
        dispatch('scheduleTrackCheckIn');
        if (!state.activeTrackingTask) {
          return null;
        }
        dispatch('actionTrackCheckIn', {
          id: state.activeTrackingTask.id,
          time: dayjs().utc(),
        });
        return null;
      }, 60 * 1000);
      return null;
    },
    changeFavicon(_, status) {
      if (!window.APP_TRON) {
        const icon = FAVICONS[status] || FAVICONS.default;
        const favicon = document.querySelector("link[rel*='icon']");
        favicon.href = icon;
      } else {
        // TODO change tray icon
      }
      return null;
    },
    changeTitle(_, title) {
      document.title = title;
    },
    // hovering the notice means reading it: it stays until the user closes it
    holdNotifyWindow() {
      clearTimeout(trackStartedCloseTimer);
    },
  },
};
